<template>
  <!-- File work in progress, in the header like the problem log: uploads and
       conversions on the media server, including the ones that started in a
       dialog that has since been closed. -->
  <div class="process-log">
    <button
      class="process-button"
      ref="button"
      :class="{ open, quiet: active === 0 }"
      v-on:click.stop="toggle"
      :title="
        active > 0
          ? active + ' file process(es) running — click to see them'
          : 'File processes — uploads and conversions are listed here while they run'
      "
    >
      <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
        <path
          d="M12 4V1L8 5l4 4V6a6 6 0 0 1 6 6c0 1.1-.3 2.1-.8 3l1.5 1.5A8 8 0 0 0 12 4zm0 14a6 6 0 0 1-6-6c0-1.1.3-2.1.8-3L5.3 7.5A8 8 0 0 0 12 20v3l4-4-4-4v3z"
        />
      </svg>
      {{ active > 0 ? active : "·" }}
    </button>

    <Teleport to="body">
      <div class="process-panel" v-if="open" :style="panelStyle">
        <div class="process-empty" v-if="entries.length === 0">
          No file uploads or conversions right now. Whatever is uploaded or
          processed on the media server shows up here until it is done.
        </div>
        <div class="process-head" v-else>
          <strong>File processes</strong>
          <span class="process-hint">
            Newest last. Finished entries stay for a while, then clear
            themselves.
          </span>
        </div>
        <div class="process-list" v-if="entries.length > 0">
          <div class="process-row" v-for="entry in ordered" :key="entry.id">
            <span class="process-kind" :class="'type-' + entry.type">
              {{ entry.type === "video" ? "VID" : "IMG" }}
            </span>
            <span class="process-body">
              <span class="process-title">{{ entry.name }}</span>
              <span class="process-status" :class="'status-' + entry.status">
                {{ label(entry) }}
              </span>
              <span
                class="process-progress"
                v-if="entry.status === 'converting' && entry.progress_total > 0"
              >
                <span
                  class="process-progress-bar"
                  :style="{
                    width:
                      Math.min(
                        100,
                        (entry.progress / entry.progress_total) * 100
                      ) + '%',
                  }"
                ></span>
              </span>
              <span
                class="process-error"
                v-if="entry.status === 'failed' && entry.error"
              >
                {{ entry.error }}
              </span>
              <span class="process-meta">
                {{ relativeTime(entry) }}
                <template v-if="entry.size">
                  · {{ humanSize(entry.size) }}
                </template>
              </span>
            </span>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  getProcesses,
  onProcesses,
  startProcessLog,
  stopProcessLog,
} from "../modules/process-log";
const LABELS = {
  uploading: "Uploading",
  processing: "Processing",
  converting: "Converting",
  ok: "Done",
  failed: "Failed",
};

export default {
  name: "ProcessPanel",
  data() {
    return {
      entries: getProcesses(),
      open: false,
      unsubscribe: null,
      panelStyle: {},
      now: Date.now(),
      clock: null,
    };
  },
  computed: {
    active() {
      return this.entries.filter((entry) =>
        ["uploading", "processing", "converting"].includes(entry.status)
      ).length;
    },
    ordered() {
      const rank = (entry) =>
        ["uploading", "processing", "converting"].includes(entry.status)
          ? 0
          : 1;
      return [...this.entries].sort((a, b) => {
        const byRank = rank(a) - rank(b);
        if (byRank !== 0) {
          return byRank;
        }
        return String(a.updated_at).localeCompare(String(b.updated_at));
      });
    },
  },
  mounted() {
    this.unsubscribe = onProcesses((entries) => {
      this.entries = [...entries];
    });
    this.clock = setInterval(() => {
      this.now = Date.now();
    }, 30000);
    startProcessLog();
    document.addEventListener("click", this.onDocumentClick);
  },
  unmounted() {
    this.unsubscribe?.();
    clearInterval(this.clock);
    stopProcessLog();
    document.removeEventListener("click", this.onDocumentClick);
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.placePanel();
      }
    },
    placePanel() {
      const rect = this.$refs.button?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      this.panelStyle = {
        position: "fixed",
        top: `${Math.round(rect.bottom + 8)}px`,
        right: `${Math.round(window.innerWidth - rect.right)}px`,
      };
    },
    onDocumentClick(event) {
      if (!this.open) {
        return;
      }
      if (!event.target?.closest?.(".process-panel")) {
        this.open = false;
      }
    },
    label(entry) {
      return LABELS[entry.status] ?? entry.status;
    },
    relativeTime(entry) {
      const when = new Date(entry.updated_at).getTime();
      const seconds = Math.max(0, Math.round((this.now - when) / 1000));
      if (seconds < 60) {
        return "just now";
      }
      if (seconds < 3600) {
        return Math.floor(seconds / 60) + " min ago";
      }
      if (seconds < 86400) {
        return Math.floor(seconds / 3600) + " h ago";
      }
      return Math.floor(seconds / 86400) + " d ago";
    },
    humanSize(bytes) {
      const units = ["B", "KB", "MB", "GB"];
      let value = Number(bytes) || 0;
      let unit = 0;
      while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit++;
      }
      return `${Math.round(value * 10) / 10} ${units[unit]}`;
    },
  },
};
</script>

<style scoped>
.process-log {
  position: relative;
}
.process-button {
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  border-radius: 999px;
  padding: 4px 10px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}
.process-button.open,
.process-button:hover {
  background: #bfdbfe;
}
.process-button.quiet {
  background: #eef1f5;
  color: #94a3b8;
}
.process-button.quiet:hover {
  background: #e2e8f0;
  color: #64748b;
}
.process-button svg {
  display: block;
}
.process-button:not(.quiet) svg {
  animation: spin 3s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.process-empty {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
}
.process-panel {
  position: fixed;
  width: 420px;
  max-width: 80vw;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  padding: 12px;
  z-index: 4000;
}
.process-head {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}
.process-hint {
  font-size: 11.5px;
  color: #94a3b8;
}
.process-list {
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.process-row {
  display: flex;
  gap: 8px;
  padding: 6px;
  border-radius: 8px;
  background: #f8fafc;
}
.process-kind {
  flex: none;
  align-self: flex-start;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  padding: 2px 5px;
  border-radius: 5px;
  background: #e2e8f0;
  color: #475569;
}
.type-video {
  background: #ede9fe;
  color: #5b21b6;
}
.type-image {
  background: #ccfbf1;
  color: #115e59;
}
.process-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}
.process-title {
  font-size: 12.5px;
  font-weight: 600;
  color: #16181d;
  word-break: break-word;
}
.process-status {
  align-self: flex-start;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 5px;
  background: #e2e8f0;
  color: #475569;
}
.status-uploading {
  background: #dbeafe;
  color: #1e40af;
}
.status-processing {
  background: #fae8ff;
  color: #86198f;
}
.status-converting {
  background: #fef3c7;
  color: #92400e;
}
.status-ok {
  background: #dcfce7;
  color: #166534;
}
.status-failed {
  background: #fee2e2;
  color: #991b1b;
}
.process-progress {
  display: block;
  height: 5px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}
.process-progress-bar {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #f59e0b;
  transition: width 0.4s ease;
}
.process-error {
  font-size: 11.5px;
  color: #991b1b;
  word-break: break-word;
}
.process-meta {
  font-size: 11px;
  color: #94a3b8;
}
</style>
